// Hardcoded yes/no review questions surfaced on the RA Detail page.
// Pre-review sits between Summary and Hazards; post-review sits between
// Hazards and Signatures. All outcomes are advisory — answers never block
// sign-off. To change wording, edit this file (single source of truth).
//
// Schema persisted per RA in risk_assessments.{pre,post}_review_answers (TEXT):
//   { [question.key]: { answer: 'yes'|'no'|null, note: string } }

export const PRE_REVIEW_QUESTIONS = [
  {
    key: 'activity_level',
    isGate: true,
    label: 'Activity Level',
    intro: 'Initial review before detailed HRN assessment begins.',
    question:
      'Does this activity involve elevated risk, e.g. unusual methodology, stunt performance, significant stored energy, vehicle movement or increased production exposure beyond normal departmental activities?',
    noOutcome: 'Standard departmental HRN assessment and controls may be sufficient.',
    // NO on the gate = lower-risk activity, proceed normally — render the
    // outcome callout in green rather than amber/red.
    noTone: 'positive',
  },
  {
    key: 'stakeholders',
    label: 'Stakeholders',
    question:
      'Are key stakeholders appropriately aware of and aligned with the proposed methodology?',
    noOutcome: 'Further stakeholder review or approval may be required before proceeding.',
  },
  {
    key: 'alternative_methods',
    label: 'Alternative Methods',
    question: 'Have lower-risk alternatives been reasonably considered?',
    noOutcome: 'Consider whether a lower-risk approach could achieve an acceptable outcome.',
  },
  {
    key: 'technical_confidence',
    label: 'Technical Readiness',
    question:
      'Does the team possess the required competencies and technical understanding to safely develop and execute the activity in its proposed form?',
    noOutcome: 'Further technical review, training, testing or redesign may be required.',
  },
];

export const POST_REVIEW_QUESTIONS = [
  {
    key: 'residual_risk',
    isGate: true,
    label: 'Residual Risk',
    intro: 'Final residual-risk check before operational sign-off.',
    question:
      'Is the remaining residual risk considered low enough for standard operational sign-off?',
    noOutcome: 'Further reduction or justification of residual risk may be required before sign-off.',
    // Gate flip: the FOLLOW-UP justification question only opens when the
    // gate is answered NO (residual is NOT low enough → needs further
    // justification or reduction). YES on the gate stops here (sign-off).
    showRestOn: 'no',
    // When NO is selected, append a prominent warning at the bottom of the
    // section (below all questions) so the assessor is reminded to escalate
    // before sign-off, regardless of how the justification question is answered.
    noBottomNotice: {
      title: 'Important Notice',
      body: 'Where elevated residual risk remains, appropriate stakeholders should be informed before final sign-off.',
    },
  },
  {
    key: 'residual_justification',
    label: 'Residual Justification',
    question:
      'Is the remaining residual risk considered justified for the intended practical or cinematic outcome?',
    noOutcome: 'Further reduction, redesign or cancellation should be considered.',
  },
];

// Returns true when every applicable question in a review section has a
// concrete yes/no answer. Honours the gate-question pattern: if the gate is
// answered in the direction that does NOT open the follow-ups, the section is
// complete with just the gate answered. Otherwise every follow-up must also
// have an answer.
export function areAnswersComplete(questions, answers) {
  if (!Array.isArray(questions) || questions.length === 0) return false;
  if (!answers || typeof answers !== 'object' || Array.isArray(answers)) return false;
  const gate = questions[0];
  const gateAnswer = answers[gate.key]?.answer;
  if (gateAnswer !== 'yes' && gateAnswer !== 'no') return false;
  const showRestOn = gate.showRestOn || 'yes';
  // Gate answered in the "stop here" direction — no follow-ups required.
  if (gateAnswer !== showRestOn) return true;
  // Follow-ups required — every one needs a yes/no answer.
  return questions.slice(1).every((q) => {
    const a = answers[q.key]?.answer;
    return a === 'yes' || a === 'no';
  });
}

// Trim notes + drop blank entries so we never persist {answer:null,note:''} cruft.
export function sanitiseAnswers(answers) {
  if (!answers || typeof answers !== 'object' || Array.isArray(answers)) return {};
  const out = {};
  for (const [key, val] of Object.entries(answers)) {
    if (!val || typeof val !== 'object') continue;
    const answer = val.answer === 'yes' || val.answer === 'no' ? val.answer : null;
    const note = typeof val.note === 'string' ? val.note.trim() : '';
    if (answer === null && note === '') continue;
    out[key] = { answer, note };
  }
  return out;
}
